// Phase 84.M.37 — Client-side image resize trước khi upload.
// Mục tiêu:
//   • Max cạnh dài 1200px (giữ tỉ lệ).
//   • JPEG quality 0.8 (~~70% reduction so với original).
//   • Giảm payload 2-5MB → ~300-500KB → upload nhanh + cost OCR thấp.

const DEFAULT_MAX = 1200;
const DEFAULT_QUALITY = 0.8;

/**
 * Resize 1 File (image) → trả về { blob, dataUrl, mime, width, height }.
 * Nếu cạnh dài <= maxEdge, KHÔNG re-encode để giữ chất lượng gốc; trả
 * nguyên file (chuyển thành dataUrl).
 */
export async function resizeImageFile(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  if (!file) throw new Error("File rỗng");
  if (!file.type?.startsWith("image/")) {
    throw new Error("File không phải ảnh");
  }

  const img = await loadImage(file);
  const longest = Math.max(img.width, img.height);

  // Nếu ảnh đã nhỏ + định dạng đã JPEG/PNG/WebP → giữ nguyên (không re-encode).
  if (longest <= maxEdge && file.size <= maxEdge * 1024) {
    const dataUrl = await fileToDataUrl(file);
    return {
      blob: file,
      dataUrl,
      mime: file.type,
      width: img.width,
      height: img.height,
      reduced: 0,
    };
  }

  const scale = Math.min(1, maxEdge / longest);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  // HEIC/HEIF không native — convert sang JPEG.
  const outMime = file.type === "image/png" && file.size < 500 * 1024 ? "image/png" : "image/jpeg";
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Canvas to Blob fail"))), outMime, quality);
  });

  const dataUrl = await blobToDataUrl(blob);
  return {
    blob,
    dataUrl,
    mime: outMime,
    width: w,
    height: h,
    reduced: Math.max(0, 1 - blob.size / file.size),
  };
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(new Error("Không đọc được ảnh"));
    };
    img.src = url;
  });
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? r.result : "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? r.result : "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/**
 * Helper for the Self Rating modal: chuyển đổi sang { data: <base64 no prefix>, mime }
 * format mà BE đang dùng.
 */
export async function resizedFileToBase64Payload(file, opts = {}) {
  const { dataUrl, mime } = await resizeImageFile(file, opts);
  const comma = dataUrl.indexOf(",");
  return { data: dataUrl.slice(comma + 1), mime };
}
